import { AdmCompany, AdmProject } from "@custom-types/base-types";
import { createSlice } from "@reduxjs/toolkit";
import { FetchingStatus } from "@store/store-helper";
import {
  fetchCompany,
  fetchCompanyMembers,
  fetchCompanyProjects,
  fetchCompanyGroups,
  fetchCompanyMigrationStatus,
  updateCompany,
} from "@store/workspace/workspace-thunks";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { removeMemberFromCompany } from "@store/member/member-thunks";

/** State for workspace */
type WorkspaceState = {
  /** The selected company */
  company: AdmCompany | null;

  /** The members of the selected company */
  members: FaroCoreApiTypes.AdmCompanyMemberWithAuth[];

  /** The projects of the selected company */
  projects: AdmProject[];

  /** The groups of the selected company */
  groups: FaroCoreApiTypes.AdmCompanyGroup[];

  /** Whether the selected workspace has been migrated from HoloBuilder to Sphere XG */
  isHbToSxgMigrated: boolean;

  /** Async operation statuses for workspace-slice */
  fetchingCompany: {
    /** The status of fetching the selected company */
    selectedCompany: FetchingStatus;

    /** The status of updating the company */
    updatingCompany: FetchingStatus;

    /** The status of fetching the members of the company */
    companyMembers: FetchingStatus;

    /** The status of fetching the projects of the company */
    companyProjects: FetchingStatus;

    /** The status of fetching the groups of the company */
    companyGroups: FetchingStatus;

    /** The status of fetching the migration status of the company */
    migrationStatus: FetchingStatus;

    /** The status of deleting a member from the company */
    deletingMember: FetchingStatus;
  };
};

/** Initial state for workspace slice */
const initialCompanyState: WorkspaceState = {
  /** By default, no company is selected */
  company: null,

  /** By default, no members are loaded for the selected company */
  members: [],

  /** By default, no projects are loaded for the selected company */
  projects: [],

  /** By default, no groups are loaded for the selected company */
  groups: [],

  /** By default, the migration status is unknown and assumed to be not migrated */
  isHbToSxgMigrated: false,

  fetchingCompany: {
    selectedCompany: "uninitialized",
    updatingCompany: "uninitialized",
    companyMembers: "uninitialized",
    companyProjects: "uninitialized",
    companyGroups: "uninitialized",
    migrationStatus: "uninitialized",
    deletingMember: "uninitialized",
  },
};

/** Slice to manage company state */
const companySlice = createSlice({
  name: "company",
  initialState: initialCompanyState,
  reducers: {
    /** Resets the company state to its initial values */
    resetCompanyState: () => initialCompanyState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompany.pending, (state) => {
        state.fetchingCompany.selectedCompany = "pending";
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.company = action.payload;
        state.fetchingCompany.selectedCompany = "succeeded";
      })
      .addCase(fetchCompany.rejected, (state) => {
        state.fetchingCompany.selectedCompany = "rejected";
      })

      .addCase(updateCompany.pending, (state) => {
        state.fetchingCompany.updatingCompany = "pending";
      })
      .addCase(updateCompany.rejected, (state) => {
        state.fetchingCompany.updatingCompany = "rejected";
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        if (state.company) {
          state.company = { ...state.company, ...action.payload };
        }
        state.fetchingCompany.updatingCompany = "succeeded";
      })

      .addCase(fetchCompanyMembers.pending, (state) => {
        state.fetchingCompany.companyMembers = "pending";
      })
      .addCase(fetchCompanyMembers.rejected, (state) => {
        state.fetchingCompany.companyMembers = "rejected";
      })
      .addCase(fetchCompanyMembers.fulfilled, (state, action) => {
        state.members = action.payload;
        state.fetchingCompany.companyMembers = "succeeded";
      })

      .addCase(fetchCompanyProjects.pending, (state) => {
        state.fetchingCompany.companyProjects = "pending";
      })
      .addCase(fetchCompanyProjects.rejected, (state) => {
        state.fetchingCompany.companyProjects = "rejected";
      })
      .addCase(fetchCompanyProjects.fulfilled, (state, action) => {
        state.projects = action.payload;
        state.fetchingCompany.companyProjects = "succeeded";
      })

      .addCase(fetchCompanyGroups.pending, (state) => {
        state.fetchingCompany.companyGroups = "pending";
      })
      .addCase(fetchCompanyGroups.rejected, (state) => {
        state.fetchingCompany.companyGroups = "rejected";
      })
      .addCase(fetchCompanyGroups.fulfilled, (state, action) => {
        state.groups = action.payload;
        state.fetchingCompany.companyGroups = "succeeded";
      })

      .addCase(fetchCompanyMigrationStatus.pending, (state) => {
        state.isHbToSxgMigrated = false;
        state.fetchingCompany.migrationStatus = "pending";
      })
      .addCase(fetchCompanyMigrationStatus.fulfilled, (state, action) => {
        state.isHbToSxgMigrated = action.payload;
        state.fetchingCompany.migrationStatus = "succeeded";
      })
      .addCase(fetchCompanyMigrationStatus.rejected, (state) => {
        state.isHbToSxgMigrated = false;
        state.fetchingCompany.migrationStatus = "rejected";
      })

      .addCase(removeMemberFromCompany.pending, (state) => {
        state.fetchingCompany.deletingMember = "pending";
      })
      .addCase(removeMemberFromCompany.fulfilled, (state, action) => {
        if (state.company?.id === action.payload.companyId) {
          state.members = state.members.filter(
            (member) => member.identity !== action.payload.userIdentity
          );
        }
        state.fetchingCompany.deletingMember = "succeeded";
      })
      .addCase(removeMemberFromCompany.rejected, (state) => {
        state.fetchingCompany.deletingMember = "rejected";
      });
  },

  selectors: {
    /** Selector to get the entire company state */
    companyStateSelector: (state) => state,
  },
});

export const { resetCompanyState } = companySlice.actions;

export const { companyStateSelector } = companySlice.selectors;

export const companyReducer = companySlice.reducer;
